import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Search, Briefcase, Users, Award, CheckCircle2, Quote, Star, Handshake, Sparkles } from "lucide-react";
import { INDUSTRIES } from "../lib/industries";
import { toast } from "sonner";

const WHY = [
  { icon: CheckCircle2, t: "Quality Candidates", d: "Relevant and screened profiles based on your requirements." },
  { icon: Briefcase, t: "Faster Hiring", d: "Dedicated recruitment support from requirement to joining." },
  { icon: Sparkles, t: "Industry Expertise", d: "Recruitment support across Engineering, Manufacturing, IT, Finance, HR, Sales, Pharma & more." },
  { icon: Users, t: "Personalized Support", d: "Dedicated recruitment assistance based on your specific requirements." },
  { icon: Award, t: "Career Opportunities", d: "Connect candidates with relevant opportunities based on their skills and experience." },
  { icon: Handshake, t: "Trusted Partnership", d: "Building long-term relationships with companies and candidates." },
];

const TESTIMONIALS = [
  { name: "Rohan Mehta", role: "HR Head, TechCorp India", quote: "Achievers delivered 12 senior engineers in 6 weeks — quality and speed no other agency matched.", stars: 5 },
  { name: "Sneha Kapoor", role: "Software Engineer", quote: "I got 3 offers in 2 weeks. My recruiter genuinely cared about the culture fit, not just the placement.", stars: 5 },
  { name: "Vikram Iyer", role: "CEO, MediLife Pharma", quote: "For pharma production roles, they're unmatched. Deep domain understanding + GMP-experienced candidates.", stars: 5 },
];

export default function Home() {
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [jobCounts, setJobCounts] = useState({});

  useEffect(() => {
    api.post("/seed").catch(() => {});
    api.get("/industries").then(r => setJobCounts(Object.fromEntries(r.data.map(i => [i.name, i.jobs])))).catch(() => {});
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", contactForm);
      toast.success("Message sent — we'll get back within 24 hours.");
      setContactForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F2A4A]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1623051786552-e46ef84e6c07" alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 ae-hero-overlay" />
        </div>
        <div className="relative ae-container py-24 md:py-36">
          <div className="max-w-3xl">
            <div className="ae-overline mb-5">Recruitment & HR Solutions in Ahmedabad</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.1] tracking-tight">
              Connecting <span className="font-serif italic text-[#C5A059]">Great Talent</span><br/>
              With <span className="font-serif italic text-[#C5A059]">Great Opportunities</span>
            </h1>
            <div className="mt-6 space-y-2 text-white/80 text-lg max-w-2xl leading-relaxed">
              <p><b className="text-white">For Employers:</b> Find the right people for your business.</p>
              <p><b className="text-white">For Candidates:</b> Discover opportunities that match your skills and experience.</p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/for-employers" className="ae-btn-primary flex items-center gap-2" data-testid="hero-cta-hire">
                Hire Talent <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/jobs" className="border-2 border-white text-white hover:bg-white hover:text-[#0F2A4A] rounded-xl px-6 py-3 font-medium transition-all flex items-center gap-2" data-testid="hero-cta-jobs">
                Find Jobs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Search bar */}
            <div className="mt-10 bg-white/95 backdrop-blur rounded-2xl p-3 shadow-2xl max-w-3xl">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="flex-1 flex items-center gap-2 px-4">
                  <Search className="w-5 h-5 text-slate-400" />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Job title, skill or company"
                    data-testid="hero-search-q"
                    className="flex-1 py-3 bg-transparent outline-none text-[#0F2A4A] text-sm" />
                </div>
                <div className="hidden md:block w-px bg-slate-200 my-2" />
                <div className="flex-1 flex items-center gap-2 px-4">
                  <input value={loc} onChange={e => setLoc(e.target.value)} placeholder="Location"
                    data-testid="hero-search-loc"
                    className="flex-1 py-3 bg-transparent outline-none text-[#0F2A4A] text-sm" />
                </div>
                <Link to={`/jobs?q=${encodeURIComponent(q)}&location=${encodeURIComponent(loc)}`}
                  data-testid="hero-search-submit"
                  className="ae-btn-secondary text-sm">Search Jobs</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="pt-24 pb-24 bg-[#F8FAFC]" data-testid="industries-section">
        <div className="ae-container">
          <div className="text-center mb-16">
            <div className="ae-overline mb-3">Industries We Serve</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A] max-w-2xl mx-auto">
              Deep specialization across <span className="font-serif italic text-[#C5A059]">12+ verticals</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {INDUSTRIES.map((i, idx) => (
              <Link to={`/jobs?industry=${encodeURIComponent(i.name)}`} key={i.name} data-testid={`industry-${idx}`}
                className="ae-card p-6 text-center group cursor-pointer hover:-translate-y-1 hover:border-[#C5A059] transition-[transform,border-color] duration-300">
                <div className="w-14 h-14 mx-auto rounded-xl bg-[#0F2A4A] group-hover:bg-[#C5A059] flex items-center justify-center transition-colors mb-3">
                  <i.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm font-medium text-[#0F2A4A]">{i.name}</div>
                <div className="mt-1 text-[11px] text-slate-500 group-hover:text-[#C5A059] transition-colors" data-testid={`industry-count-${idx}`}>
                  {jobCounts[i.name] ? `${jobCounts[i.name]} open job${jobCounts[i.name] > 1 ? "s" : ""}` : "View jobs"} →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="ae-container">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="ae-overline mb-3">Why Achievers Enterprise</div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A] leading-tight">
                A trusted recruitment partner in <span className="font-serif italic text-[#C5A059]">Ahmedabad</span>
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Whether you're a company looking to hire the right people or a professional looking for the right role — we help both sides connect with clarity and care.
              </p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-[#C5A059] font-medium hover:gap-3 transition-all">
                Learn our story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-8 grid md:grid-cols-2 gap-4">
              {WHY.map((w, i) => (
                <div key={i} className="ae-card p-6" data-testid={`why-${i}`}>
                  <w.icon className="w-6 h-6 text-[#C5A059] mb-3" />
                  <h3 className="font-heading font-medium text-[#0F2A4A] mb-2">{w.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{w.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#0F2A4A] text-white relative overflow-hidden">
        <div className="ae-container relative">
          <div className="text-center mb-16">
            <div className="ae-overline mb-3">Testimonials</div>
            <h2 className="text-3xl md:text-4xl font-medium">
              Trusted by <span className="font-serif italic text-[#C5A059]">India's best</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8" data-testid={`testimonial-${i}`}>
                <Quote className="w-8 h-8 text-[#C5A059] mb-4" />
                <p className="text-white/90 leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.stars }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />)}
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-white/60">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-24 bg-white" data-testid="contact-section">
        <div className="ae-container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="ae-overline mb-3">Get in touch</div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A] leading-tight">
                Ready to <span className="font-serif italic text-[#C5A059]">accelerate</span> your hiring?
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Whether you're hiring 1 senior leader or 100 engineers, our team is here to help. Reach out and we'll respond within 24 hours.
              </p>
              <div className="mt-8 space-y-4 text-sm">
                <div className="flex items-center gap-3"><Briefcase className="w-5 h-5 text-[#C5A059]" /> achieversenterpriseinfo@gmail.com</div>
                <div className="flex items-center gap-3"><Users className="w-5 h-5 text-[#C5A059]" /> +91 90990 42472 · +91 83200 92044</div>
              </div>
            </div>
            <form onSubmit={handleContact} className="ae-card p-8 space-y-4" data-testid="home-contact-form">
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="Full Name" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                  data-testid="contact-name" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all px-4 py-3 text-sm" />
                <input required type="email" placeholder="Email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all px-4 py-3 text-sm" />
              </div>
              <input placeholder="Phone" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                data-testid="contact-phone" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all px-4 py-3 text-sm" />
              <input required placeholder="Subject" value={contactForm.subject} onChange={e => setContactForm({ ...contactForm, subject: e.target.value })}
                data-testid="contact-subject" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all px-4 py-3 text-sm" />
              <textarea required rows={4} placeholder="How can we help?" value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                data-testid="contact-message" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all px-4 py-3 text-sm" />
              <button type="submit" className="ae-btn-primary w-full" data-testid="contact-submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
